import { useState } from 'react';
import { Link } from 'react-router-dom';
import { userApi } from '../../api/client';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Button, Badge, Card, CardHeader, ErrorAlert, SkeletonCard, EmptyState } from '../../components/ui';
import { ChevronLeft, ChevronRight, ClipboardList, Coins, Mail } from 'lucide-react';
import { getReviewTypeConfig } from '../../lib/reviewTypes';

// Helper to get date 1 year ago in YYYY-MM-DD format
const getOneYearAgo = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 1);
  return date.toISOString().split('T')[0];
};

// Helper to get today's date in YYYY-MM-DD format
const getToday = () => {
  return new Date().toISOString().split('T')[0];
};

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStatusBadgeVariant(status: string): 'blue' | 'yellow' | 'green' | 'red' | 'gray' {
  switch (status) {
    case 'claimed': return 'blue';
    case 'submitted': return 'yellow';
    case 'approved': return 'green';
    case 'rejected': return 'red';
    default: return 'gray';
  }
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'claimed': return 'Claimed';
    case 'submitted': return 'Submitted';
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    default: return status;
  }
}

export function MyTasksPage() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [startDate, setStartDate] = useState(getOneYearAgo());
  const [endDate, setEndDate] = useState(getToday());

  const { data, isLoading, isError, error, refetch } = useApiQuery({
    queryKey: ['my-tasks', page, statusFilter, startDate, endDate],
    queryFn: () => userApi.getMyTasks(page, 20, statusFilter || undefined, startDate, endDate),
    refetchOnMount: 'always',
  });

  const filters = [
    { value: '', label: 'All' },
    { value: 'claimed', label: 'Claimed' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row items-center md:justify-between gap-2">
          <h1 className="w-full md:w-auto text-2xl font-bold text-gray-900 text-left">My Tasks</h1>
          <div className="flex flex-wrap gap-2">
            {filters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => {
                  setStatusFilter(filter.value);
                  setPage(1);
                }}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all ${
                  statusFilter === filter.value
                    ? 'bg-primary-600 text-white shadow-sm'
                    : 'border border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <label className="text-gray-600">From:</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setPage(1);
              }}
              className="input py-1 px-2 text-sm"
            />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-gray-600">To:</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setPage(1);
              }}
              className="input py-1 px-2 text-sm"
            />
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4">
          {[...Array(3)].map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : isError ? (
        <ErrorAlert message={error?.message} onRetry={() => refetch()} />
      ) : data?.participations.length === 0 ? (
        <EmptyState
          icon={ClipboardList}
          title={statusFilter
            ? `No tasks with status "${filters.find((f) => f.value === statusFilter)?.label}"`
            : "You haven't joined any tasks yet."}
          description="Browse available tasks and start earning credits."
          action={
            <Link to="/tasks">
              <Button>Browse Available Tasks</Button>
            </Link>
          }
        />
      ) : (
        <>
          <Card className="overflow-hidden">
            <CardHeader className="!py-3 !px-5 bg-gray-50">
              <div className="flex items-center min-w-[600px]">
                <div className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wide">Task Name</div>
                <div className="w-28 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</div>
                <div className="w-24 text-xs font-semibold text-gray-500 uppercase tracking-wide">Credits</div>
                <div className="w-36 text-xs font-semibold text-gray-500 uppercase tracking-wide">Date</div>
              </div>
            </CardHeader>
            <div className="overflow-x-auto">
              {data?.participations.map((participation) => {
                const reviewConfig = getReviewTypeConfig(participation.review_type);
                const ReviewIcon = reviewConfig.icon;

                return (
                  <Link
                    key={participation.id}
                    to={`/tasks/${participation.task_id}`}
                    className="flex items-center px-5 py-3.5 border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors min-w-[600px]"
                  >
                    {/* Task Name */}
                    <div className="flex-1 flex items-center gap-3 min-w-0">
                      <div
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: reviewConfig.bg }}
                      >
                        <ReviewIcon className="w-4 h-4" style={{ color: reviewConfig.color }} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{participation.task_name}</p>
                        {participation.google_email && (
                          <p className="text-xs text-gray-500 flex items-center gap-1 mt-0.5">
                            <Mail className="w-3 h-3" />
                            <span className="truncate">{participation.google_email}</span>
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Status */}
                    <div className="w-28">
                      <Badge variant={getStatusBadgeVariant(participation.status)}>
                        {getStatusLabel(participation.status)}
                      </Badge>
                    </div>

                    {/* Credits */}
                    <div className="w-24 flex items-center gap-1.5">
                      <div className="w-5 h-5 rounded-full bg-amber-100 flex items-center justify-center">
                        <Coins className="w-3 h-3 text-amber-600" />
                      </div>
                      <span className="text-sm font-medium text-gray-700">{participation.credit_reward}</span>
                    </div>

                    {/* Date */}
                    <div className="w-36 text-sm text-gray-500">
                      {formatDate(participation.created_at)}
                    </div>
                  </Link>
                );
              })}
            </div>
          </Card>

          {/* Pagination */}
          {data?.pagination && data.pagination.pages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Showing {(page - 1) * 20 + 1} to{' '}
                {Math.min(page * 20, data.pagination.total)} of{' '}
                {data.pagination.total} tasks
              </p>
              <div className="flex space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.pages}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
